import type { Feature, FeatureCollection, GeoJSON, Geometry, GeometryCollection, Position } from 'geojson';

/**
 * Calculates the bounding box for any GeoJSON object.
 *
 * This function computes the minimum and maximum longitude and latitude values
 * that encompass all coordinates in the GeoJSON geometry. The result can be used
 * directly with Mapbox GL JS's `fitBounds` method or `bounds` option.
 *
 * @param geojson - A GeoJSON object of any type (Feature, FeatureCollection, or Geometry)
 * @returns A tuple in the format [minLng, minLat, maxLng, maxLat] representing the bounding box
 *
 * @example
 * ```typescript
 * const geojson: GeoJSON = {
 *   type: 'Point',
 *   coordinates: [-73.9857, 40.7484]
 * };
 *
 * const bounds = calculateBounds(geojson);
 * // Returns: [-73.9857, 40.7484, -73.9857, 40.7484]
 *
 * // Use with Mapbox
 * map.fitBounds(bounds, { padding: 50 });
 * ```
 *
 * @remarks
 * Supports all GeoJSON geometry types including:
 * - Point, LineString, Polygon
 * - MultiPoint, MultiLineString, MultiPolygon
 * - GeometryCollection
 * - Feature and FeatureCollection
 *
 * @throws May throw if the GeoJSON contains no valid coordinates
 */
export function calculateBounds(geojson: GeoJSON): [number, number, number, number] {
  let coords: Array<Array<number>> = new Array<Array<number>>();

  // Handle different GeoJSON types
  if (geojson.type === 'FeatureCollection') {
    (geojson as FeatureCollection).features.reduce(
      (currentCoords: Array<Array<number>>, feature: Feature): Array<Array<number>> => {
        currentCoords.push(...extractCoordinates(feature.geometry as Geometry));
        return currentCoords;
      },
      coords
    );
  } else if (geojson.type === 'GeometryCollection') {
    (geojson as GeometryCollection).geometries.reduce(
      (currentCoords: Array<Array<number>>, geometry: Geometry): Array<Array<number>> => {
        currentCoords.push(...extractCoordinates(geometry));
        return currentCoords;
      },
      coords
    );
  } else if (geojson.type === 'Feature') {
    coords = extractCoordinates(geojson.geometry);
  } else {
    coords = extractCoordinates(geojson);
  }

  const lngs: Array<number> = coords.map((coordinate: Array<number>): number => coordinate[0]);
  const lats: Array<number> = coords.map((coordinate: Array<number>): number => coordinate[1]);

  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
}

/**
 * Extracts all coordinate pairs from a GeoJSON geometry.
 *
 * This is a helper function that recursively traverses a geometry structure
 * and flattens all coordinate arrays into a single array of [longitude, latitude] pairs.
 * It handles nested structures like Polygons (with holes) and MultiPolygons.
 *
 * @param geometry - A GeoJSON Geometry or GeometryCollection object
 * @returns A flat array of coordinate pairs, where each coordinate is [longitude, latitude]
 *
 * @example
 * ```typescript
 * const polygon: Polygon = {
 *   type: 'Polygon',
 *   coordinates: [
 *     [[-73.9857, 40.7484], [-73.9837, 40.7494], [-73.9847, 40.7504], [-73.9857, 40.7484]]
 *   ]
 * };
 *
 * const coords = extractCoordinates(polygon);
 * // Returns: [[-73.9857, 40.7484], [-73.9837, 40.7494], [-73.9847, 40.7504], [-73.9857, 40.7484]]
 * ```
 *
 * @remarks
 * This function uses a switch statement to handle each geometry type:
 * - **Point**: Single coordinate
 * - **LineString/MultiPoint**: Array of coordinates
 * - **Polygon/MultiLineString**: Array of rings/lines (each is an array of coordinates)
 * - **MultiPolygon**: Array of polygons (nested arrays)
 * - **GeometryCollection**: Recursively processes each geometry
 *
 * @internal This is typically used as a helper function by `calculateBounds`
 */
function extractCoordinates(geometry: Geometry | GeometryCollection): Array<Array<number>> {
  const coordinates: Array<Array<number>> = new Array<Array<number>>();

  switch (geometry.type) {
    case 'Point':
      coordinates.push(geometry.coordinates);
      break;
    case 'LineString':
    case 'MultiPoint':
      coordinates.push(...geometry.coordinates);
      break;
    case 'Polygon':
    case 'MultiLineString':
      geometry.coordinates.forEach((ring: Array<Position>): void => {
        coordinates.push(...ring);
      });
      break;
    case 'MultiPolygon':
      geometry.coordinates.forEach((polygon: Array<Array<Position>>): void => {
        polygon.forEach((ring: Array<Position>): void => {
          coordinates.push(...ring);
        });
      });
      break;
    case 'GeometryCollection':
      geometry.geometries.forEach(extractCoordinates);
      break;
  }

  return coordinates;
}
